<script lang="ts" setup>
import type { Tab } from '~/composables/useTabs'

const props = defineProps<{
  tabs: Record<string, Tab>
  view: string
  defaultTabKey?: string
  textClass?: string
  navClasses?: string
  vertical?: boolean
  showIndex?: boolean
}>()
const emit = defineEmits<{ (e: 'click', key: string): void }>()

const tabsArray = computed(() => Object.values(props.tabs))

const tabAmount = computed(() => tabsArray.value.filter(({ hidden }) => !hidden).length)
const fontSize = computed(() => {
  if (props.textClass) {
    return props.textClass
  }
  const tabAmount = Object.keys(props.tabs).length
  if (tabAmount > 5) {
    return 'xl:text-sm'
  }
  return 'xl:text-base'
})

const tabsForSelect = computed(() => Object.values(props.tabs).filter(tab => !tab.hidden).map(tab => ({
  label: tab.label,
  value: tab.key,
})))

function scrollToId(id?: string) {
  const element = document.getElementById(id ?? '')
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' })
  }
}
// When using collapse-able sections, we need to set a small timeout for the card to expand
const waitAndScrollToId = (id?: string) => setTimeout(() => scrollToId(id), 100)

const tabMode = computed(() => props.tabs[props.view]?.mode)
onMounted(() => {
  if (tabMode.value === 'scroll' && props.view !== props.defaultTabKey) {
    emit('click', props.view)
    waitAndScrollToId(props.view)
  }
})
</script>

<template>
  <div v-if="tabAmount > 1" class="mb-4 mt-8 md:mt-0" :class="tabMode === 'scroll' ? 'hidden' : 'xl:hidden'">
    <label for="tabs" class="sr-only">Select a tab</label>
    <n-select
      :value="view"
      :options="tabsForSelect"
      size="large"
      @update:value="(key: string) => {
        $emit('click', key)
        $router.push({ query: { ...$route.query, view: key } })
      }"
    />
  </div>
  <template v-if="tabAmount > 1">
    <nav
      class="hidden xl:flex isolate divide-x-2 divide-gray-100 rounded mb-4 border sticky top-2 z-10"
      aria-label="Tabs"
      :class="[
        navClasses,
        {
          'flex-col divide-y-2': vertical,
          'shadow-sm': !vertical,
        },
      ]"
    >
      <NuxtLink
        v-for="(tab, tabIdx) in tabsArray"
        :key="tab.label"
        :to="!tab.disabled ? { query: { ...$route.query, view: tab.key } } : undefined"
        class="group relative min-w-0 flex-1 overflow-hidden p-4 text-black bg-white hover:bg-gray-100"
        :class="{
          'cursor-not-allowed hover:bg-transparent hover:text-gray-500': tab.disabled,
          'rounded-l-lg': tabIdx === 0 && !vertical,
          'rounded-r-lg': tabIdx === tabsArray.length - 1 && !vertical,
          'rounded-t-lg': tabIdx === 0 && vertical,
          'rounded-b-lg': tabIdx === tabsArray.length - 1 && vertical,
          'hidden': tab.hidden,
        }"
        :aria-current="tab.current ? 'page' : undefined"
        @click="() => {
          if (tab.key) {
            $emit('click', tab.key)
            if (tab.mode === 'scroll') {
              waitAndScrollToId(tab.key)
            }
          }
        }"
      >
        <p :class="{ 'text-center': !vertical, fontSize }">
          <span v-if="showIndex">
            {{ tabIdx + 1 }}.
          </span>
          {{ tab.label }}
        </p>
        <div
          aria-hidden="true"
          class="absolute inset-x-0 bottom-0 h-0.5"
          :class="[tab.current ? 'bg-black' : 'bg-transparent']"
        />
      </NuxtLink>
    </nav>
  </template>
  <slot />
</template>
